import { Injectable } from '@angular/core';
import { stringifyPagerOptions } from '../../../util/http-helpers';
import { ConfigService } from '../config/config.service';
import { HttpClient, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { Device, DeviceDeleteManyRequest, DeviceUpdateManyRequest, PagerOptions } from '../../app.types';
import { HttpErrorService } from '../http-error.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class DeviceManagementService {
  cachedPagerOptions: PagerOptions;

  /**
   * CTOR
   * @param {HttpClient} http
   * @param {ConfigService} configService
   * @param {HttpErrorService} httpErrorService
   * @param toastrService
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private httpErrorService: HttpErrorService,
    private toastrService: ToastrService
  ) {}

  getDevice(id: string) : Observable<Device> {
    const url = this.configService.API_URI + '/Devices/' + id;

    const req = new HttpRequest('GET', url);

    return this.http.request<Device>(req).pipe(
      filter((res) => res instanceof HttpResponse),
      map((res) => {
        return (res as HttpResponse<Device>).body;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Gets all devices
   * @param {PagerOptions} opts
   * @returns {Observable<any>}
   */
  getDevices(opts: PagerOptions) {
    let url = this.configService.API_URI + '/Devices';

    // Stringify pagerOptions
    const optionsString = stringifyPagerOptions(opts);
    if (optionsString !== null) {url += optionsString;}

    const req = new HttpRequest('GET', url);

    return this.http.request(req).pipe(
      map((res) => {
        return res;
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Gets every device matching the supplied filters, ignoring paging. Used by the
   * export so that all currently-filtered devices are returned, not just the current page.
   * @param {PagerOptions} opts the current filter/search/order state (paging is ignored server-side)
   * @returns {Observable<Device[]>}
   */
  getAllDevicesForExport(opts: PagerOptions): Observable<Device[]> {
    let url = this.configService.API_URI + '/Devices/Export';

    // Stringify pagerOptions (only search/filter/orderBy/ascending are relevant here)
    const optionsString = stringifyPagerOptions(opts);
    if (optionsString !== null) {url += optionsString;}

    const req = new HttpRequest<Device[]>('GET', url);

    return this.http.request<Device[]>(req).pipe(
      filter((res) => res instanceof HttpResponse),
      map((res) => (res as HttpResponse<Device[]>).body),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Gets all devices
   * @param {PagerOptions} opts
   * @returns {Observable<any>}
   */
  getLiveDevices(opts: PagerOptions) {
    let url = this.configService.API_URI + '/Devices/GetLive';

    // Stringify pagerOptions
    const optionsString = stringifyPagerOptions(opts);
    if (optionsString !== null) {url += optionsString;}

    return this.http.get(url).pipe(
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Adds a Device
   * @returns {Observable<any>}
   * @param device
   */
  addDevice(device: Device) {
    const url = this.configService.API_URI + '/Devices';

    const req = new HttpRequest('POST', url, device);

    return this.http.request(req).pipe(
      map((res) => {
        if (res instanceof HttpResponse) {
          this.toastrService.success('Device added.', 'Success');
          return res;
        }
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Edits a Device
   * @returns {Observable<any>}
   * @param device
   */
  editDevice(device: Device) {
    const url = this.configService.API_URI + '/Devices/' + device.id;

    const req = new HttpRequest('PUT', url, device);

    return this.http.request(req).pipe(
      map((res) => {
        if (res instanceof HttpResponse) {
          this.toastrService.success('Device updated.', 'Success');
          return res;
        }
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Delete a Device
   * @returns {Observable<any>}
   * @param device
   */
  deleteDevice(deviceId: string) {
    const url = this.configService.API_URI + '/Devices/' + deviceId;

    const req = new HttpRequest('DELETE', url);

    return this.http.request(req).pipe(
      map((res) => {
        if (res instanceof HttpResponse) {
          this.toastrService.success('Device deleted.', 'Success');
          return res;
        }
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Delete many Device
   * @returns {Observable<any>}
   * @param request
   */
  deleteDevices(request: DeviceDeleteManyRequest) {
    const url = this.configService.API_URI + '/Devices/DeleteMany';
    
    const req = new HttpRequest('POST', url, request);

    return this.http.request(req).pipe(
      map((res) => {
        if (res instanceof HttpResponse) {
          this.toastrService.success('Devices deleted.', 'Success');
          return res;
        }
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }

  /**
   * Update many Devices
   * @returns {Observable<any>}
   * @param request
   */
  updateDevices(request: DeviceUpdateManyRequest) {
    const url = this.configService.API_URI + '/Devices/UpdateMany';
    
    const req = new HttpRequest('POST', url, request);

    return this.http.request(req).pipe(
      map((res) => {
        if (res instanceof HttpResponse) {
          this.toastrService.success('Devices updated.', 'Success');
          return res;
        }
      }),
      catchError((err) => {
        this.httpErrorService.handleHttpError(err);
        return of(err);
      })
    );
  }
}
